import React, { useState } from "react";
import { Link, useLocation, Navigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCog, Building2, Globe2, Briefcase, Award,
  ShieldCheck, MessageSquare, Bot, LogOut, Menu, X, MapPin, ChevronDown, BookOpen, GraduationCap, FileText, Wallet, Settings, BarChart3,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../mock";
import AccountMenu from "../components/AccountMenu";
import NotificationBell from "../components/NotificationBell";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, kinds: ["super_admin", "employee", "agent"] },
  { to: "/admin/leads", label: "Leads CRM", icon: Users, kinds: ["super_admin", "employee", "agent"] },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, kinds: ["super_admin", "employee"] },
  { to: "/admin/applications", label: "Applications", icon: FileText, kinds: ["super_admin", "employee"] },
  { to: "/admin/approvals", label: "Approvals & Hierarchy", icon: ShieldCheck, kinds: ["super_admin", "employee"] },
  { to: "/admin/chat", label: "Team Chat", icon: MessageSquare, kinds: ["super_admin", "employee", "agent"] },
  { to: "/admin/ai-logs", label: "AI Counsellor Logs", icon: Bot, kinds: ["super_admin", "employee"] },
  { to: "/admin/books", label: "Emerald Books", icon: Wallet, kinds: ["super_admin", "employee"] },
  { section: "Masters", kinds: ["super_admin", "employee"] },
  { to: "/admin/countries", label: "Countries", icon: Globe2, kinds: ["super_admin"] },
  { to: "/admin/companies", label: "Companies", icon: Building2, kinds: ["super_admin", "employee"] },
  { to: "/admin/offices", label: "Offices", icon: MapPin, kinds: ["super_admin", "employee"] },
  { to: "/admin/roles", label: "Roles", icon: ShieldCheck, kinds: ["super_admin", "employee"] },
  { to: "/admin/designations", label: "Designations", icon: Award, kinds: ["super_admin", "employee"] },
  { to: "/admin/employees", label: "Employees", icon: UserCog, kinds: ["super_admin", "employee"] },
  { to: "/admin/agents", label: "Agents", icon: Briefcase, kinds: ["super_admin", "employee"] },
  { to: "/admin/teachers", label: "Teachers", icon: BookOpen, kinds: ["super_admin", "employee"] },
  { to: "/admin/students", label: "Students", icon: GraduationCap, kinds: ["super_admin", "employee"] },
  { to: "/admin/edu-courses", label: "Course Catalog", icon: BookOpen, kinds: ["super_admin", "employee"] },
  { to: "/admin/streams", label: "Streams", icon: BookOpen, kinds: ["super_admin", "employee"] },
  { to: "/admin/fields", label: "Fields / Specialisations", icon: BookOpen, kinds: ["super_admin", "employee"] },
  { to: "/admin/universities", label: "Universities", icon: GraduationCap, kinds: ["super_admin", "employee"] },
  { to: "/admin/institutions", label: "Institutions", icon: Building2, kinds: ["super_admin", "employee"] },
  { to: "/admin/schools", label: "Schools", icon: Building2, kinds: ["super_admin", "employee"] },
  { to: "/admin/previous-schools", label: "Previous Schools", icon: Building2, kinds: ["super_admin", "employee"] },
  { to: "/admin/odl-courses", label: "ODL Courses", icon: BookOpen, kinds: ["super_admin", "employee"] },
  { to: "/admin/certificates", label: "Certificates", icon: ShieldCheck, kinds: ["super_admin", "employee"] },
  { to: "/admin/fee-master", label: "Fee Master", icon: Wallet, kinds: ["super_admin", "employee"] },
  { to: "/admin/parents", label: "Parents", icon: Users, kinds: ["super_admin", "employee"] },
  { to: "/admin/guardians", label: "Guardians", icon: Users, kinds: ["super_admin", "employee"] },
  { to: "/admin/guardian-relations", label: "Guardian Relationships", icon: Users, kinds: ["super_admin", "employee"] },
  { to: "/admin/currencies", label: "Currencies", icon: Globe2, kinds: ["super_admin"] },
  { to: "/admin/testimonials", label: "Testimonials", icon: Award, kinds: ["super_admin", "employee"] },
  { section: "System", kinds: ["super_admin"] },
  { to: "/admin/settings", label: "Site Settings", icon: Settings, kinds: ["super_admin"] },
];

const KIND_LABEL = { super_admin: "Administrator", employee: "Employee", agent: "Agent" };

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-[#004030]">Loading…</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.kind === "student") return <Navigate to="/portal/student" replace />;
  if (user.kind === "teacher") return <Navigate to="/portal/teacher" replace />;

  const items = NAV.filter((n) => n.kinds.includes(user.kind));

  return (
    <div className="min-h-screen bg-[#f4f8f6] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#013d2b] text-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <a href="/" className="h-16 flex items-center gap-3 px-5 border-b border-white/10 hover:bg-white/5" data-testid="console-logo-home">
          <img src={BRAND.logo} alt="GDIH" className="h-9 bg-white/95 rounded p-0.5" />
          <span className="font-display font-bold">GDIH Console</span>
        </a>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {items.map((n, i) =>
            n.section ? (
              <p key={i} className="text-[10px] uppercase tracking-[0.2em] text-white/40 px-3 pt-5 pb-2">{n.section}</p>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === n.to ? "bg-[#c9a227] text-[#013d2b]" : "text-white/80 hover:bg-white/10"
                }`}
              >
                <n.icon size={18} /> {n.label}
              </Link>
            )
          )}
        </nav>
        <button onClick={logout} className="m-3 flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-white/80 hover:bg-white/10">
          <LogOut size={18} /> Sign out
        </button>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-5 sticky top-0 z-20">
          <button className="lg:hidden text-[#004030]" onClick={() => setOpen(true)}><Menu size={24} /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <AccountMenu />
          </div>
        </header>
        <main className="flex-1 p-5 md:p-7 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
